import { useNavigate } from "react-router-dom";
import { ChevronRight, Flame, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import InfoSheet from "../common/InfoSheet";
import type { HomePageData } from "../../services/HomeService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import {
  measureCardHeading,
  measureCardMeta,
  measureCardValueSubhead,
  gluceraHomeEyebrow,
  gluceraHomeMeta,
  gluceraHomeStatusBadge,
} from "../../utils/gluceraTypography";
import {
  homeDonutStrokeClass,
  homeDonutTrackClass,
  homeLabelBadgeClass,
  homeScoreTextClass,
  homeStreakBadge,
} from "../../utils/homeBlueTheme";

export interface HomeBbsScoreSectionProps {
  homeData?: HomePageData;
  selectedDate: string;
  className?: string;
  /** When true, renders without outer card shell (for combined BBS + pillars card). */
  embedded?: boolean;
}

function getLabelBadgeClass(label?: string) {
  return homeLabelBadgeClass(label);
}

function getDonutStrokeClass(label?: string) {
  return homeDonutStrokeClass(label);
}

function getDonutTrackClass() {
  return homeDonutTrackClass();
}

function getScoreTextClass(label?: string) {
  return homeScoreTextClass(label);
}

const HomeBbsScoreSection = ({
  homeData,
  selectedDate,
  className,
  embedded = false,
}: HomeBbsScoreSectionProps) => {
  const navigate = useNavigate();
  const bbs = homeData?.["Bite Balance Score"];
  const dailyScore = bbs?.daily_bbs;

  if (dailyScore == null) {
    return null;
  }

  const dailyScoreDisplay = String(Math.round(dailyScore));
  const dailyLabel = bbs?.label;
  const mealsLogged = bbs?.meals_logged ?? 0;
  const streakDays = homeData?.["Streak"]?.current_days ?? 0;

  const radius = 34;
  const stroke = 5;
  const normalizedRadius = radius - stroke;
  const circumference = normalizedRadius * 2 * Math.PI;
  const progress = Math.min((dailyScore / 100) * 100, 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const strokeClass = getDonutStrokeClass(dailyLabel);
  const trackClass = getDonutTrackClass();
  const scoreTextClass = getScoreTextClass(dailyLabel);

  const metaParts = [
    mealsLogged > 0 ? `${mealsLogged} meal${mealsLogged === 1 ? "" : "s"}` : null,
    bbs?.provisional ? "Provisional" : null,
  ].filter(Boolean);

  const handleOpenDetail = () => {
    navigate("/glucera" + GluceraNavigationRoutes.BBS_DETAIL, {
      state: { selectedDate },
    });
  };

  const scoreBody = (
    <div className="relative flex w-full items-center gap-3 p-4 text-left">
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${radius * 2} ${radius * 2}`}
            className="rotate-[-90deg]"
            aria-hidden
          >
            <circle
              className={trackClass}
              strokeWidth={stroke}
              fill="transparent"
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            <circle
              className={strokeClass}
              strokeWidth={stroke}
              strokeDasharray={`${circumference} ${circumference}`}
              style={{ strokeDashoffset }}
              strokeLinecap="round"
              fill="transparent"
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
          </svg>
          <div className="absolute flex flex-col items-center tabular-nums">
            <span className={cn("text-sm font-bold leading-none", scoreTextClass)}>
              {dailyScoreDisplay}
            </span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={gluceraHomeEyebrow}>Overall today</p>
            {streakDays > 0 ? (
              <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1", homeStreakBadge)}>
                <Flame className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
                {streakDays} day{streakDays === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <span className={measureCardHeading}>Bite Balance Score</span>
              <InfoSheet
                title="Bite Balance Score"
                ariaLabel="About Bite Balance Score"
                trigger={
                  <button
                    type="button"
                    aria-label="About Bite Balance Score"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                }
              >
                <div className="space-y-3">
                  <p>
                    Your <span className="font-semibold text-foreground">Bite Balance Score (BBS)</span> is a
                    daily 0–100 score showing how balanced your day was across your
                    four pillars: <span className="font-semibold text-foreground">Glucose</span>,{" "}
                    <span className="font-semibold text-foreground">Microbiome</span>,{" "}
                    <span className="font-semibold text-foreground">Physical activity</span> and{" "}
                    <span className="font-semibold text-foreground">Weight</span>.
                  </p>
                  <div className="space-y-1.5">
                    <p className="font-bold text-foreground">Score bands:</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#737DFE]" />
                        Excellent — 80–100
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#737DFE]/70" />
                        Good — 65–79
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#6268e8]" />
                        Caution / Fair — 45–64
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#5258d4]" />
                        Needs work — below 45
                      </div>
                    </div>
                  </div>
                  <p>
                    Log more meals through the day for a more accurate score. Tap the
                    arrow to see a full breakdown.
                  </p>
                </div>
              </InfoSheet>
            </span>
            <span className={cn(measureCardValueSubhead, scoreTextClass)}>
              {dailyScoreDisplay}/100
            </span>
          </div>
          {metaParts.length > 0 ? (
            <p className={cn("mt-1", gluceraHomeMeta)}>{metaParts.join(" · ")}</p>
          ) : null}
          {dailyLabel ? (
            <span
              className={cn(
                "mt-2 inline-flex rounded-full px-2.5 py-1",
                gluceraHomeStatusBadge,
                getLabelBadgeClass(dailyLabel)
              )}
            >
              {dailyLabel}
            </span>
          ) : null}
        </div>

        <button
          type="button"
          onClick={handleOpenDetail}
          aria-label="Open Bite Balance Score details"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={2} aria-hidden />
        </button>
    </div>
  );

  if (embedded) {
    return <div className={className}>{scoreBody}</div>;
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60",
        className,
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#737DFE]/[0.07] to-transparent dark:from-[#737DFE]/[0.12]"
        aria-hidden
      />
      {scoreBody}
    </div>
  );
};

export default HomeBbsScoreSection;
